import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getConsultantProfile, updateConsultantProfile, getAuthToken } from '../logic/consultantProfile/consultantProfileApi';
import { ConsultantProfileFormState } from '../logic/consultantProfile/types';
import SelectDropdown from '../components/SelectDropdown';

const ConsultantProfilePage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [originalData, setOriginalData] = useState<ConsultantProfileFormState | null>(null);

  // Form state
  const [formData, setFormData] = useState<ConsultantProfileFormState>({
    consultantId: '',
    status: 'active',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
    professionalDetails: {
      primarySpecialization: '',
      experienceYears: 0,
      areasOfExpertise: [],
      professionalSummary: '',
      philosophy: ''
    },
    contactAvailability: {
      professionalEmail: '',
      officePhone: '',
      officeHours: '',
      languages: [],
      availabilityNotes: ''
    }
  });

  // Load consultant profile data on mount
  useEffect(() => {
    loadConsultantProfile();
  }, []);

  const loadConsultantProfile = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken();
      if (!token) {
        setSaveMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      const response = await getConsultantProfile(token);
      if (response.success && response.data) {
        const profileData = response.data;
        const formState: ConsultantProfileFormState = {
          consultantId: profileData.consultantId || '',
          status: profileData.status || 'active',
          emergencyContact: {
            name: profileData.emergencyContact?.name || '',
            relationship: profileData.emergencyContact?.relationship || '',
            phone: profileData.emergencyContact?.phone || ''
          },
          professionalDetails: {
            primarySpecialization: profileData.professionalDetails?.primarySpecialization || '',
            experienceYears: profileData.professionalDetails?.experienceYears || 0,
            areasOfExpertise: profileData.professionalDetails?.areasOfExpertise || [],
            professionalSummary: profileData.professionalDetails?.professionalSummary || '',
            philosophy: profileData.professionalDetails?.philosophy || ''
          },
          contactAvailability: {
            professionalEmail: profileData.contactAvailability?.professionalEmail || '',
            officePhone: profileData.contactAvailability?.officePhone || '',
            officeHours: profileData.contactAvailability?.officeHours || '',
            languages: profileData.contactAvailability?.languages || [],
            availabilityNotes: profileData.contactAvailability?.availabilityNotes || ''
          }
        };

        setFormData(formState);
        setOriginalData(formState);
      }
    } catch (error) {

      setSaveMessage({ type: 'error', text: 'Failed to load profile data' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form field updates
  const updateFormField = (path: string, value: any) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current: any = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Handle adding expertise
  const addExpertise = (expertise: string) => {
    if (expertise.trim() && !formData.professionalDetails.areasOfExpertise.includes(expertise.trim())) {
      updateFormField('professionalDetails.areasOfExpertise', [
        ...formData.professionalDetails.areasOfExpertise,
        expertise.trim()
      ]);
    }
  };

  // Handle removing expertise
  const removeExpertise = (expertiseToRemove: string) => {
    updateFormField('professionalDetails.areasOfExpertise',
      formData.professionalDetails.areasOfExpertise.filter(exp => exp !== expertiseToRemove)
    );
  };

  // Handle adding language
  const addLanguage = (language: string) => {
    if (language.trim() && !formData.contactAvailability.languages.includes(language.trim())) {
      updateFormField('contactAvailability.languages', [
        ...formData.contactAvailability.languages,
        language.trim()
      ]);
    }
  };

  // Handle removing language
  const removeLanguage = (languageToRemove: string) => {
    updateFormField('contactAvailability.languages',
      formData.contactAvailability.languages.filter(lang => lang !== languageToRemove)
    );
  };


  // Handle save
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveMessage(null);

      const token = getAuthToken();
      if (!token) {
        setSaveMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      // Convert form data to API format
      const apiData = {
        consultantProfile: {
          ...formData
        }
      };

      const response = await updateConsultantProfile(token, apiData);

      if (response.success) {
        setSaveMessage({ type: 'success', text: 'Profile updated successfully' });
        setOriginalData(formData);
      } else {
        setSaveMessage({ type: 'error', text: response.message || 'Failed to update profile' });
      }
    } catch (error) {

      setSaveMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (originalData) {
      setFormData(originalData);
      setSaveMessage(null);
    }
  };

  if (isLoading) {
    return (
      <div className={`consultant-profile ${isDarkMode ? 'dark' : ''}`}>
        <div className="consultant-profile__container">
          <div className="consultant-profile__loading">Loading consultant profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`consultant-profile ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-profile__container">
        {/* Header Section */}
        <header className="consultant-profile__header">
          <h1 className="consultant-profile__title">Consultant Profile</h1>
          <p className="consultant-profile__subtitle">
            Manage your professional credentials, expertise areas, and contact information to help your clients find and connect with you.
          </p>
        </header>

        {/* Main Form Content */}
        <form className="consultant-profile__form">
          <div className="consultant-profile__form-card">
            {/* Credentials Section */}
            <section className="consultant-profile__form-section">
              <div className="consultant-profile__form-section-header">
                <span className="material-symbols-outlined consultant-profile__section-icon">badge</span>
                <h2 className="consultant-profile__form-section-title">Credentials</h2>
              </div>

              <h3 className="consultant-profile__subsection-title">License Information</h3>

              <div className="form-control">
                <label className="form-control__label">
                  Consultant ID <span className="form-control__required">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.consultantId}
                  onChange={(e) => updateFormField('consultantId', e.target.value)}
                  placeholder="Enter your ID number"
                  aria-label="Consultant ID"
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">
                  Status <span className="form-control__required">*</span>
                </label>
                <div className="form-radio-group">
                  <label className="form-radio-group__option">
                    <input
                      type="radio"
                      name="status"
                      checked={formData.status === 'active'}
                      onChange={() => updateFormField('status', 'active')}
                      aria-label="Active status"
                    />
                    <span className="form-radio-group__option__dot"></span>
                    <span className="form-radio-group__option__label">Active</span>
                  </label>
                  <label className="form-radio-group__option">
                    <input
                      type="radio"
                      name="status"
                      checked={formData.status === 'inactive'}
                      onChange={() => updateFormField('status', 'inactive')}
                      aria-label="Inactive status"
                    />
                    <span className="form-radio-group__option__dot"></span>
                    <span className="form-radio-group__option__label">Inactive</span>
                  </label>
                </div>
              </div>

              <div className="consultant-profile__form-divider"></div>

              {/* Emergency Contact Section */}
              <h3 className="consultant-profile__subsection-title">Emergency Contact</h3>

              <div className="form-control">
                <label className="form-control__label">
                  Name <span className="form-control__required">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.emergencyContact.name}
                  onChange={(e) => updateFormField('emergencyContact.name', e.target.value)}
                  placeholder="Contact full name"
                  aria-label="Emergency contact name"
                />
              </div>

              <div className="consultant-profile__form-row">
                <div className="form-control">
                  <label className="form-control__label">Relationship</label>
                  <SelectDropdown
                    value={formData.emergencyContact.relationship}
                    onChange={(value) => updateFormField('emergencyContact.relationship', value)}
                    options={[
                      { value: '', label: 'Select relationship' },
                      { value: 'Spouse', label: 'Spouse' },
                      { value: 'Parent', label: 'Parent' },
                      { value: 'Sibling', label: 'Sibling' },
                      { value: 'Other', label: 'Other' }
                    ]}
                    ariaLabel="Emergency contact relationship"
                  />
                </div>

                <div className="form-control">
                  <label className="form-control__label">Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.emergencyContact.phone}
                    onChange={(e) => updateFormField('emergencyContact.phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    aria-label="Emergency contact phone"
                  />
                </div>
              </div>
            </section>

            {/* Professional Details Section */}
            <section className="consultant-profile__form-section">
              <div className="consultant-profile__form-section-header">
                <span className="material-symbols-outlined consultant-profile__section-icon">school</span>
                <h2 className="consultant-profile__form-section-title">Professional Details</h2>
              </div>

              <div className="consultant-profile__form-row">
                <div className="form-control">
                  <label className="form-control__label">Primary Specialization</label>
                  <SelectDropdown
                    value={formData.professionalDetails.primarySpecialization}
                    onChange={(value) => updateFormField('professionalDetails.primarySpecialization', value)}
                    options={[
                      { value: '', label: 'Select specialization' },
                      { value: 'Clinical Psychology', label: 'Clinical Psychology' },
                      { value: 'Family Therapy', label: 'Family Therapy' },
                      { value: 'Cognitive Behavioral', label: 'Cognitive Behavioral' }
                    ]}
                    ariaLabel="Primary specialization"
                  />
                </div>

                <div className="form-control">
                  <label className="form-control__label">Experience (Years)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.professionalDetails.experienceYears}
                    onChange={(e) => updateFormField('professionalDetails.experienceYears', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    aria-label="Years of experience"
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="form-control__label">Areas of Expertise</label>
                <div className="pill-list">
                  {formData.professionalDetails.areasOfExpertise.map((expertise, index) => (
                    <span key={index} className="pill-list__pill">
                      {expertise}
                      <button
                        type="button"
                        onClick={() => removeExpertise(expertise)}
                        aria-label={`Remove ${expertise}`}
                        title={`Remove ${expertise}`}
                        className="pill-list__pill-btn"
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary consultant-profile__add-expertise-btn"
                    onClick={() => {
                      const newExpertise = prompt('Add area of expertise:');
                      if (newExpertise) addExpertise(newExpertise);
                    }}
                    aria-label="Add new area of expertise"
                  >
                    <span className="material-symbols-outlined">add</span> Add Expertise
                  </button>
                </div>
              </div>

              <div className="form-control">
                <div className="form-textarea-counter">
                  <label className="form-control__label">Professional Summary</label>
                  <span className="form-textarea-counter__count">
                    {formData.professionalDetails.professionalSummary.length} / 500
                  </span>
                </div>
                <textarea
                  className="form-textarea"
                  value={formData.professionalDetails.professionalSummary}
                  onChange={(e) => updateFormField('professionalDetails.professionalSummary', e.target.value)}
                  placeholder="Brief overview of your career and key achievements..."
                  maxLength={500}
                  rows={4}
                  aria-label="Professional summary"
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">Philosophy</label>
                <textarea
                  className="form-textarea"
                  value={formData.professionalDetails.philosophy}
                  onChange={(e) => updateFormField('professionalDetails.philosophy', e.target.value)}
                  placeholder="Describe your professional philosophy and approach..."
                  rows={3}
                  aria-label="Professional philosophy"
                />
              </div>
            </section>

            {/* Contact & Availability Section */}
            <section className="consultant-profile__form-section">
              <div className="consultant-profile__form-section-header">
                <span className="material-symbols-outlined consultant-profile__section-icon">contact_mail</span>
                <h2 className="consultant-profile__form-section-title">Contact &amp; Availability</h2>
              </div>

              <div className="form-control">
                <label className="form-control__label">Professional Email</label>
                <div className="consultant-profile__phone-input">
                  <span className="material-symbols-outlined consultant-profile__phone-input-icon">mail</span>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.contactAvailability.professionalEmail}
                    onChange={(e) => updateFormField('contactAvailability.professionalEmail', e.target.value)}
                    placeholder="professional@example.com"
                    aria-label="Professional email"
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="form-control__label">Office Phone</label>
                <div className="consultant-profile__phone-input">
                  <span className="consultant-profile__phone-input__prefix">
                    <img
                      src="https://lh3.googleusercontent.com/aida-public/AB6AXuAm3e1a7ugvgookXS9Tp0n9bTAMq18St3gLNB1MhSwDggt17bHWsheFOO0L4S4FCqksAeUXkwqmXcidmPQNY6o0fjiQDn0L4-msh8bAqcUIlOdQOhZo4wxJH0FMYKujAhkxj_4gMOnOI6XKWd_ooL9gVHYbs5oztSxdW6ycASCIrkD2i69zstTvEM9yblku7BHnylqp-MyBQUCbjfqRbJsaYd4FscVJiacV2xkmK_couS-NyOZxizPAksUXO-gGj8PENJmUUoqxmBk"
                      alt="Philippines"
                      style={{ width: '16px', marginRight: '4px' }}
                    />
                    +63
                  </span>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.contactAvailability.officePhone}
                    onChange={(e) => updateFormField('contactAvailability.officePhone', e.target.value)}
                    placeholder="917 123 4567"
                    aria-label="Office phone"
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="form-control__label">Office Hours</label>
                <textarea
                  className="form-textarea"
                  value={formData.contactAvailability.officeHours}
                  onChange={(e) => updateFormField('contactAvailability.officeHours', e.target.value)}
                  placeholder="Example: Mon-Fri: 9:00 AM - 5:00 PM"
                  rows={3}
                  aria-label="Office hours"
                />
              </div>

              <div className="form-control">
                <label className="form-control__label">Languages Spoken</label>
                <div className="pill-list">
                  {formData.contactAvailability.languages.map((language, index) => (
                    <span key={index} className="pill-list__pill">
                      {language}
                      <button
                        type="button"
                        onClick={() => removeLanguage(language)}
                        aria-label={`Remove ${language}`}
                        title={`Remove ${language}`}
                        className="pill-list__pill-btn"
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary consultant-profile__add-language-btn"
                    onClick={() => {
                      const newLanguage = prompt('Add language:');
                      if (newLanguage) addLanguage(newLanguage);
                    }}
                    aria-label="Add new language"
                  >
                    <span className="material-symbols-outlined">add</span> Add Language
                  </button>
                </div>
              </div>

              <div className="form-control">
                <label className="form-control__label">Availability Notes</label>
                <textarea
                  className="form-textarea"
                  value={formData.contactAvailability.availabilityNotes}
                  onChange={(e) => updateFormField('contactAvailability.availabilityNotes', e.target.value)}
                  placeholder="Notes about scheduling preferences or specific off-days..."
                  rows={3}
                  aria-label="Availability notes"
                />
              </div>
            </section>
          </div>
        </form>

        {/* Action Buttons Section */}
        <section className="consultant-profile__actions">
          <div className="consultant-profile__actions-left">
            <button
              type="button"
              className="btn btn-secondary consultant-profile__export-btn"
              onClick={() => {/* Export functionality could be added here */}}
              title="Export your profile as PDF"
              aria-label="Export profile"
            >
              <span className="material-symbols-outlined">ios_share</span>
              <span>Export Profile</span>
            </button>
          </div>
          <div className="consultant-profile__actions-right">
            {saveMessage && (
              <div className={`consultant-profile__message consultant-profile__message--${saveMessage.type}`}
                role="status"
                aria-live="polite"
              >
                {saveMessage.text}
              </div>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={isSaving}
              aria-label="Cancel changes"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving}
              aria-label="Save profile changes"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ConsultantProfilePage;
